import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

// Toasts live in one polite live region; errors stay until dismissed or 6 s, others 3 s (R9).
type Tone = 'info' | 'error' | 'order';
interface ToastItem {
  id: number;
  text: string;
  tone: Tone;
}

const ToastContext = createContext<((text: string, tone?: Tone) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (text: string, tone: Tone = 'info') => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-3), { id, text, tone }]);
      window.setTimeout(() => dismiss(id), tone === 'error' ? 6000 : tone === 'order' ? 5000 : 3000);
    },
    [dismiss],
  );

  const value = useMemo(() => push, [push]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite" aria-atomic="false">
        {items.map((t) => (
          <div key={t.id} className={t.tone === 'error' ? 'toast error' : 'toast'}>
            <Icon name={t.tone === 'error' ? 'alert' : t.tone === 'order' ? 'bell' : 'check'} />
            <span className="grow t-label">{t.text}</span>
            <button type="button" className="btn btn-quiet icon-btn" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <Icon name="close" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const v = useContext(ToastContext);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
